import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useCreateNotificationsSettingsMutation,
  useGetNotificationsSettingsListQuery,
} from 'services/notificationService';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useAppDispatch } from 'store/store';
import { selectSignUpState, setSignUpNotificationPref } from 'features/signUp/signUpSlice';
import { useSelector } from 'react-redux';
import { useLoggedInUser } from 'features/auth/hooks/useAuthData';
import { useLazyGetUserQuery } from 'services/authService';
import { useNotificationToggle } from './useNotificationToggle';
import {
  NotificationChannel,
  NotificationType,
  CreateNotificationsSettingsPayload,
} from '../types/notificationTypes';
import { NOTIFICATIONS_FEATURE_FLAG } from '../constants';

// TODO: (Remove in NotificationErrorAlert too)
// Translation for error messsage is in https://wellframe.atlassian.net/browse/WFMOB-14580

export const useNotificationSettings = ({
  // Only the single owner of the notification settings view should auto-create; other
  // consumers of this hook must not, or every mounted instance fires a duplicate POST.
  enableAutoCreate = false,
}: { enableAutoCreate?: boolean } = {}) => {
  const user = useLoggedInUser();
  const userPhoneNumber = user?.caregiver?.phone_number ?? user?.phone_number ?? '';
  const { translation } = useTranslationContext();
  const appDispatch = useAppDispatch();
  const memberUuid = user?.uuid;
  const hospitalUuid = user?.hospital?.uuid;
  const shouldSkip = !user?.uuid || !user?.hospital?.uuid;
  const { signUpNotificationPref } = useSelector(selectSignUpState);
  const [triggerGetUser] = useLazyGetUserQuery();
  const [memberSmsEnabled, setMemberSmsEnabled] = useState(false);
  const [memberLoaded, setMemberLoaded] = useState(false);
  // Guards the auto-create effect so it fires at most once and doesn't retry into 409s.
  const hasAttemptedAutoCreateRef = useRef(false);
  const {
    data: notificationSettingsList,
    isLoading: isNotificationSettingsLoading,
    refetch: refetchNotificationSettings,
    error: getNotificationSettingsError,
  } = useGetNotificationsSettingsListQuery(
    {
      memberUuid,
      hospitalUuid,
      pageIndex: 0,
      pageSize: 10,
    },
    { skip: shouldSkip },
  );
  const [createNotificationSetting, { isLoading: createNotificationSettingsSubmitting }] =
    useCreateNotificationsSettingsMutation();

  // Fetch /me directly so hospital.member_sms_enabled is fresh (the stored user can lack it).
  useEffect(() => {
    let isActive = true;
    triggerGetUser()
      .unwrap()
      .then((fetchedUser) => {
        if (isActive) {
          setMemberSmsEnabled(!!fetchedUser.hospital?.member_sms_enabled);
          setMemberLoaded(true);
        }
      })
      .catch(() => {
        if (isActive) {
          setMemberSmsEnabled(false);
          setMemberLoaded(true);
        }
      });
    return () => {
      isActive = false;
    };
  }, [triggerGetUser]);

  const smsNotificationSettings = useMemo(
    () =>
      notificationSettingsList?.find(
        (notificationSetting) =>
          notificationSetting.notification_type === NotificationType.CHAT_MESSAGE_NEW &&
          notificationSetting.notification_channel ===
            NotificationChannel.NOTIFICATION_CHANNEL_TEXT,
      ),
    [notificationSettingsList],
  );
  const memberNotificationSettings = useMemo(
    () =>
      notificationSettingsList?.find(
        (notificationSetting) =>
          notificationSetting.notification_type === NotificationType.MEMBER_NOTIFICATION_EVENT &&
          notificationSetting.notification_channel ===
            NotificationChannel.NOTIFICATION_CHANNEL_TEXT,
      ),
    [notificationSettingsList],
  );
  const [alertMessage, setAlertMessage] = useState<string>('');

  const onToggleErrorCallback = useCallback(() => {
    setAlertMessage(translation('generic_error_occurred'));
  }, []);

  const {
    notificationsEnabled,
    chatNotificationEnabled,
    memberNotificationEnabled,
    onChangeNotificationPreferences,
    isUpdateNotificatonPreferencesLoading,
  } = useNotificationToggle(
    smsNotificationSettings?.enabled ?? false,
    memberNotificationSettings?.enabled ?? false,
    memberSmsEnabled,
    onToggleErrorCallback,
    refetchNotificationSettings,
  );

  // Note: For signup flow, we also use isNotificationFlagEnabled from useSignupData
  // which is consumed in usePolicyAcceptanceStep and useProfileCreationStep
  const isNotificationFlagEnabled = useMemo(
    () =>
      user?.feature_flags?.find(
        (flag) => flag.feature_flag_type === NOTIFICATIONS_FEATURE_FLAG.CHAT_SMS,
      )?.isEnabled ?? false,
    [user?.feature_flags],
  );

  const dismissAlert = () => {
    setAlertMessage('');
  };
  const onCreateNotificationSettings = useCallback(
    async (enabledState: boolean) => {
      if (!memberUuid || !hospitalUuid) {
        setAlertMessage(translation('generic_error_occurred'));
        return;
      }

      const ntype: NotificationType = memberNotificationSettings?.enabled
        ? NotificationType.CHAT_MESSAGE_NEW
        : NotificationType.MEMBER_NOTIFICATION_EVENT;

      try {
        const response = await createNotificationSetting({
          memberUuid,
          body: {
            hospital_uuid: hospitalUuid,
            enabled: enabledState,
            // Send old response if memberSms is not enabled
            ...(!memberSmsEnabled
              ? {
                  notification_type: NotificationType.CHAT_MESSAGE_NEW,
                  notification_channel: NotificationChannel.NOTIFICATION_CHANNEL_TEXT,
                }
              : {}),
            // Create only missing type if one is present (treat null/undefined the same as false)
            ...(memberSmsEnabled &&
            (memberNotificationSettings?.enabled ?? false) !==
              (smsNotificationSettings?.enabled ?? false)
              ? {
                  notification_type: ntype,
                  notification_channel: NotificationChannel.NOTIFICATION_CHANNEL_TEXT,
                }
              : {}),
          },
        });
        if (response?.error) {
          setAlertMessage(translation('generic_error_occurred'));
          return;
        }
        refetchNotificationSettings();
      } catch (error) {
        setAlertMessage(translation('generic_error_occurred'));
      }
    },
    [
      memberUuid,
      hospitalUuid,
      createNotificationSetting,
      memberSmsEnabled,
      memberNotificationSettings,
      smsNotificationSettings,
    ],
  );

  const onCreateAllNotificationSettingsForUser = useCallback(
    async (enabledState: boolean) => {
      if (!memberUuid || !hospitalUuid) {
        setAlertMessage(translation('generic_error_occurred'));
        return;
      }

      try {
        await createNotificationSetting({
          memberUuid,
          body: {
            hospital_uuid: hospitalUuid,
            enabled: enabledState,
          },
        }).unwrap();
        refetchNotificationSettings();
      } catch (error) {
        setAlertMessage(translation('generic_error_occurred'));
      }
    },
    [
      memberUuid,
      hospitalUuid,
      createNotificationSetting,
      refetchNotificationSettings,
      translation,
      memberSmsEnabled,
    ],
  );

  const handleSignUpCreateNotificationSettings = useCallback(
    async (
      enabledState: boolean,
      signUpMemberUuid: string,
      signUpHospitalUuid: string,
      signUpIsHospitalSmsEnabled: boolean,
    ) => {
      if (!signUpMemberUuid || !signUpHospitalUuid) {
        return;
      }
      let retryFailed = false;
      let attempts = 0;
      const maxAttempts = 3;

      const body: CreateNotificationsSettingsPayload = signUpIsHospitalSmsEnabled
        ? {
            hospital_uuid: signUpHospitalUuid,
            enabled: enabledState,
          }
        : {
            hospital_uuid: signUpHospitalUuid,
            notification_channel: NotificationChannel.NOTIFICATION_CHANNEL_TEXT,
            notification_type: NotificationType.CHAT_MESSAGE_NEW,
            enabled: enabledState,
          };

      while (attempts < maxAttempts) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await createNotificationSetting({
            memberUuid: signUpMemberUuid,
            body,
          }).unwrap();
          break;
        } catch (error) {
          attempts += 1;
          if (attempts >= maxAttempts) {
            console.error('Error creating notification settings after 3 attempts:', error);
            retryFailed = true;
            break;
          }
        }
      }
      appDispatch(
        setSignUpNotificationPref({
          smsNotificationOptInEnabled: enabledState,
          retryFailed,
        }),
      );
    },
    [createNotificationSetting],
  );

  // Create notification settings if they do not exist for legacy users
  // Note: For signup flow, we also use isNotificationFlagEnabled from useSignupData
  // which is consumed in usePolicyAcceptanceStep and useProfileCreationStep
  useEffect(() => {
    const hasFetchedSettings = notificationSettingsList !== undefined;

    // Legacy (memberSms off) only needs the chat setting; member SMS needs both types.
    const isMissingRequiredSettings = memberSmsEnabled
      ? !smsNotificationSettings || !memberNotificationSettings
      : !smsNotificationSettings;

    // Wait for the /me fetch (memberLoaded) so memberSmsEnabled is settled and we POST once.
    if (
      enableAutoCreate &&
      hasFetchedSettings &&
      memberLoaded &&
      isMissingRequiredSettings &&
      memberUuid &&
      hospitalUuid &&
      (isNotificationFlagEnabled || memberSmsEnabled) &&
      !hasAttemptedAutoCreateRef.current
    ) {
      hasAttemptedAutoCreateRef.current = true;
      if (memberSmsEnabled) {
        onCreateAllNotificationSettingsForUser(false);
      } else {
        onCreateNotificationSettings(false);
      }
    }
  }, [
    enableAutoCreate,
    notificationSettingsList,
    smsNotificationSettings,
    memberNotificationSettings,
    memberSmsEnabled,
    memberLoaded,
    memberUuid,
    hospitalUuid,
    isNotificationFlagEnabled,
    onCreateAllNotificationSettingsForUser,
    onCreateNotificationSettings,
  ]);

  const onRetryAction = useCallback(() => {
    refetchNotificationSettings();
  }, [refetchNotificationSettings]);

  const serverErrorHeading = translation('generic_error_occurred');

  const serverErrorBody = translation('generic_error_occurred');

  const isCreatedNotificationSettingsError =
    (signUpNotificationPref?.retryFailed && signUpNotificationPref?.smsNotificationOptInEnabled) ??
    false;

  const createNotificationErrorMessage = translation('sms_settings_failed_toast');

  return {
    enabledInExternalSystem: smsNotificationSettings?.enabled_in_external_system ?? true,
    enabled: notificationsEnabled,
    memberSmsEnabled,
    memberLoaded,
    chatNotificationEnabled,
    memberNotificationEnabled,
    isNotificationSettingsError: getNotificationSettingsError,
    isNotificationSettingsLoading:
      createNotificationSettingsSubmitting || isNotificationSettingsLoading,
    isUpdateNotificatonPreferencesLoading,
    onChangeNotificationPreferences,
    onCreateNotificationSettings,
    onCreateAllNotificationSettingsForUser,
    handleSignUpCreateNotificationSettings,
    onRetryAction,
    serverErrorHeading,
    serverErrorBody,
    dismissAlert,
    alertMessage,
    isCreatedNotificationSettingsError,
    createNotificationErrorMessage,
    isNotificationFlagEnabled,
    userPhoneNumber,
    hospitalTwilioSmsNumber: smsNotificationSettings?.notifications_caller_id ?? '',
  };
};
